import MutableTrack from './MutableTrack.js';
import {
    loadTrackJson,
    loadTrackPatternsAndEffects
} from 'engine/json/loadTrackJson.js';
import {
    setLoadingText
} from 'actions/index.js';
import {MULTI_FREEZE} from 'constants/track-types.js';
import Sample from 'repos/Sample.js';

/**
 * A track that contains frozen versions of multiple tracks,
 * frozen together

 */
class MultiFreezeTrack extends MutableTrack {
    constructor(trackNumber, sequencerEngine, mixSession, numberOfPatterns, update, subSequence, createSynth=true) {
        super(trackNumber, sequencerEngine, mixSession, numberOfPatterns, update, subSequence);
        this.setupEffects();
    }

    getSampler() {
        // find the current playing sampler 
        let playing = this.frozenColumns[0].playing || 0;
       return this.frozenColumns[0].frozenSamplers[playing];
    }

    get sample() {
        if (!this.frozenColumns || !this.frozenColumns[0]) {
            return undefined;
        }
        let playing = this.frozenColumns[0].playing || 0;
        return this.frozenColumns[0].frozenSamples[playing];
    }

    getName() {
        return "Multi Freeze";
    }

    getTrackType() {
        return MULTI_FREEZE;
    }

    async unfreeze(frozenGroup=this.frozenGroup, frozenTracks=this.frozenTracks, frozenTrack) {
        // need to create a new group with the group settings
        // and then add all the tracks to that
        this.sequencerEngine.dispatch(
            setLoadingText("Unfreezing Tracks"));

        let currentPattern = this.currentPattern;

        let engine = this.sequencerEngine;
        let tracksAdded = [];
        let tracks = [... engine.tracks];
        for (let _track of frozenTracks) {
            if (frozenTrack && frozenTrack !== _track) {
                continue;
            }
            if (!_track) {
               tracks.push(false);
                tracksAdded.push(false);
                continue;
            }
            let {patterns, trackData, trackEffectChains, trackMidiEffects} = _track;

            let track = 
                await loadTrackJson(
                    engine,
                    trackData,
                    patterns,
                    0,
                    this.sequencerEngine.currentSubSequence
                );
            tracks.push(track);
            tracksAdded.push(track);
        }

        let counter=0;
        for (let i in frozenTracks) {
            if (frozenTrack && frozenTracks[i] !== frozenTrack) {
                continue;
            }
            let track = tracksAdded[counter++];
            if (!track || !frozenTracks[i]) {
                continue;
            }
            let {patterns, patternsConfig, trackData, trackEffectChains, trackMidiEffects} = frozenTracks[i];
            await loadTrackPatternsAndEffects({
                sequencerEngine: engine,
                track,
                tracks,
                patterns,
                trackData,
                trackEffectChains,
                trackMidiEffects,
                subSequence: engine.currentSubSequence
            });


            if (patternsConfig) {
                // need to match the # of patterns between the project and the
                // unfrozen tracks

                let numberOfPatterns = this.patterns.length;
                let trackNumberOfPatterns = patternsConfig.length;
                engine.switchToPattern(numberOfPatterns - 1);

                if (trackNumberOfPatterns > numberOfPatterns) {
                    // then we need to create new patterns for the entire project
                    // copied from the very last pattern

                    for (let i=numberOfPatterns; i < trackNumberOfPatterns; i++) {
                        await engine.newPattern(true);
                    }
                } else if (trackNumberOfPatterns < numberOfPatterns) {
                    // then we need to create patterns for the track
                    for (let i=trackNumberOfPatterns; i < numberOfPatterns; i++) {
                        await track.newPattern(true);
                    }
                }

                for (let j=0; j < numberOfPatterns; j++) {
                    track.patterns[j].loadFromJson(patternsConfig[Math.min(j, patternsConfig.length - 1)]);
                }
            }
        }

        // create the group with the settings and add all these tracks to that
        let group = this.sequencerEngine.groups.newGroup();
        tracksAdded.filter(x => x).forEach(t => t.subSequence = this.subSequence);
        tracksAdded.filter(x => x).forEach(t => group.addTrack(t));
        if (frozenGroup) {
            frozenGroup.tracks = [];
            await group.fromJson(frozenGroup, this.sequencerEngine);
        }
       this.sequencerEngine.tracks = tracks.filter(x => x);
        this.sequencerEngine.groups.updateGroupNumbers();
        this.sequencerEngine.groups.groups.forEach(
            g => g.updateRedux());

        this.sequencerEngine.switchToPattern(currentPattern);

        // update tracks seems to not be enough to make the tracks update
        this.sequencerEngine.updateTracks(true);
        this.sequencerEngine.selectTrack(
            this.sequencerEngine.tracks.length - tracksAdded.length);

        this.sequencerEngine.dispatch(
            setLoadingText(undefined));

        tracksAdded.filter(x => x).forEach(
            t => t.midiEffects.effects.filter(
                x => x.lostParameters && x.lostParameters.length > 0)
                .forEach(x => x.loadParameters(x.lostParameters)));

        if (!frozenGroup) {
            let pad = this.pad;
            tracksAdded.filter(x => x).forEach(
                (x,i) => {
                    let emptyIndex = pad.slots.indexOf(false);
                    if (emptyIndex === -1) {
                        pad = this.subSequence.createPad();
                    }
                    pad.addTrack(x, emptyIndex);
                    x.trackIsLoading = false;
                });
        }
    }

    fromJson(json) {
        super.fromJson(json);

        this.frozenGroup = json.frozenGroup;
        this.frozenTracks = json.frozenTracks;
    }

    getJson() {
        return {
            ... super.getJson(),
            frozenGroup:  this.frozenGroup,
            frozenTracks: this.frozenTracks
        };
    }
}

export default MultiFreezeTrack;
